import { InteractionRequiredAuthError, type AccountInfo, type IPublicClientApplication } from "@azure/msal-browser";
import { COPILOT_STUDIO_SCOPE, loginRequest } from "./msal";

export function accountName(account: AccountInfo | null): string {
  if (!account) {
    return "";
  }
  return account.name?.trim() || account.username?.trim() || "";
}

export function accountOid(account: AccountInfo | null): string {
  const claims = account?.idTokenClaims as { oid?: unknown; sub?: unknown } | undefined;
  if (typeof claims?.oid === "string" && claims.oid) {
    return claims.oid;
  }
  if (typeof claims?.sub === "string" && claims.sub) {
    return claims.sub;
  }
  return account?.localAccountId ?? "";
}

export async function acquireAccessToken(
  instance: IPublicClientApplication,
  scopes: string[] = ["User.Read"],
): Promise<string> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) {
    throw new Error("Not signed in");
  }
  instance.setActiveAccount(account);
  const result = await instance.acquireTokenSilent({
    scopes,
    account,
  }).catch(async (error) => {
    if (!isInteractionRequired(error)) {
      throw error;
    }
    await instance.acquireTokenRedirect({ scopes, account });
    throw error;
  });
  if (!result.accessToken) {
    throw new Error("Microsoft did not return an access token");
  }
  return result.accessToken;
}

export async function fetchGraphPhotoUrl(instance: IPublicClientApplication): Promise<string | null> {
  try {
    const token = await acquireAccessToken(instance, ["User.Read"]);
    const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function putGraphPhoto(instance: IPublicClientApplication, jpeg: Blob): Promise<void> {
  const token = await acquireAccessToken(instance, ["User.ReadWrite"]);
  const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "image/jpeg",
    },
    body: jpeg,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail.trim() || `Graph photo update failed (${response.status})`);
  }
}

export async function deleteGraphPhoto(instance: IPublicClientApplication): Promise<void> {
  const token = await acquireAccessToken(instance, ["User.ReadWrite"]);
  const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok && response.status !== 404) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail.trim() || `Graph photo delete failed (${response.status})`);
  }
}

export async function acquireCopilotStudioToken(instance: IPublicClientApplication): Promise<string> {
  try {
    return await acquireAccessToken(instance, [COPILOT_STUDIO_SCOPE]);
  } catch (error) {
    if (isInteractionRequired(error)) {
      throw new Error("Microsoft needs permission to use Ask AI. After you approve, send again.");
    }
    throw error;
  }
}

export async function acquireIdToken(instance: IPublicClientApplication): Promise<string> {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  if (!account) {
    throw new Error("Not signed in");
  }
  instance.setActiveAccount(account);
  const scopes = loginRequest.scopes ?? ["openid", "profile", "email", "User.Read"];
  const result = await instance.acquireTokenSilent({ scopes, account }).catch(async (error) => {
    if (!isInteractionRequired(error)) {
      throw error;
    }
    await instance.acquireTokenRedirect({ scopes, account });
    throw error;
  });
  const token = result.idToken || result.accessToken;
  if (!token) {
    throw new Error("Microsoft did not return an ID token");
  }
  return token;
}

export function isInteractionRequired(error: unknown): boolean {
  return error instanceof InteractionRequiredAuthError;
}
